import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { ApprovalStatusBadge } from '@/components/efs/ApprovalStatusBadge';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { MasterExportButton } from '@/components/efs/MasterExportButton';
import { IncomingItemsFormDialog } from './IncomingItemsFormDialog';
import { IncomingItemsRowActions } from './IncomingItemsRowActions';
import { useIncomingItemsList } from './useIncomingItems';
import type { IncomingItem } from './types';

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

export const IncomingItemsListPage = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [createOpen, setCreateOpen] = useState(false);
  // Audit-issue navigation intents may carry a search/highlight query
  // (spec-13 §6.2) pointing at the affected master row.
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const highlightQ = searchParams.get('q');
  const isHighlighted = (row: IncomingItem) =>
    (!!highlightId && row.incoming_item_id === highlightId) ||
    (!!highlightQ && row.japanese_name === highlightQ);

  const query = useIncomingItemsList({ page, per_page: perPage });

  const columns = useMemo<MasterTableColumn<IncomingItem>[]>(
    () => [
      {
        key: 'japanese_name',
        header: t('efs.masters.fields.japanese_name', { ns: 'masters' }),
        cell: (row) => row.japanese_name,
      },
      {
        key: 'normalized_name',
        header: t('efs.masters.fields.normalized_name', { ns: 'masters' }),
        cell: (row) => <span className="font-mono text-xs">{row.normalized_name}</span>,
      },
      {
        key: 'category_group',
        header: t('efs.masters.fields.category_group', { ns: 'masters' }),
        cell: (row) =>
          t(`efs.masters.category_group.${row.category_group}`, {
            ns: 'masters',
            defaultValue: row.category_group,
          }),
      },
      {
        key: 'effective_date',
        header: t('efs.masters.fields.effective_date', { ns: 'masters' }),
        cell: (row) => row.effective_date,
      },
      {
        key: 'default_route_type',
        header: t('efs.masters.fields.default_route_type', {
          ns: 'masters',
          defaultValue: '標準處理方式',
        }),
        cell: (row) => {
          if (!row.default_route_type) {
            return <span className="text-efs-text-muted">—</span>;
          }

          const label = t(`efs.masters.route_type.${row.default_route_type}`, {
            ns: 'masters',
            defaultValue: row.default_route_type,
          });

          return row.default_route_type === 'secondary_process' ? (
            <span className="inline-flex items-center gap-2">
              {label}
              <Badge variant="outline">
                {t('efs.masters.route_type.legacy_badge', {
                  ns: 'masters',
                  defaultValue: '履歴',
                })}
              </Badge>
            </span>
          ) : (
            label
          );
        },
      },
      {
        key: 'approval_status',
        header: t('efs.masters.fields.approval_status', { ns: 'masters' }),
        cell: (row) => <ApprovalStatusBadge status={row.approval_status} />,
      },
      {
        key: 'active_flag',
        header: t('efs.masters.fields.active', { ns: 'masters' }),
        cell: (row) => (
          <Badge variant={row.active_flag ? 'default' : 'secondary'}>
            {t(row.active_flag ? 'efs.masters.status.active' : 'efs.masters.status.inactive', {
              ns: 'masters',
            })}
          </Badge>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => <IncomingItemsRowActions row={row} />,
      },
    ],
    [t],
  );

  const rows = query.data?.rows ?? [];
  const total = query.data?.meta?.total ?? 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.masters.incoming.title', { ns: 'masters' })}
        subtitle={t('efs.masters.incoming.subtitle', { ns: 'masters' })}
        count={total}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('efs.masters.actions.create', { ns: 'masters' })}
          </Button>
        }
      />

      <TableToolbar
        count={t('efs.masters.toolbar.count', { ns: 'masters', count: total })}
        actions={<MasterExportButton kind="incoming-items" />}
      />

      <MasterTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.incoming_item_id}
        rowProps={(row) =>
          isHighlighted(row) ? { className: 'bg-efs-bg-subtle ring-1 ring-efs-accent' } : undefined
        }
        isLoading={query.isLoading}
        pagination={{
          page,
          perPage,
          total,
          onPageChange: setPage,
          onPerPageChange: (nextPerPage) => {
            setPage(1);
            setPerPage(nextPerPage);
          },
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />

      <IncomingItemsFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
};

export default IncomingItemsListPage;
